import { CalendarRange, PieChart } from 'lucide-react';
import type { SustainabilityCoverage } from '@siliconx/shared';

/**
 * SIL-442 (W4) data-coverage strip — spec §9: the trajectory "ships
 * **caveated, not gated**: renders available history with a data-coverage
 * strip (months available, % of fleet covered) + standard disclosures;
 * history deepens monthly."
 *
 * ⚠ THIS COMPONENT NEVER GATES ANYTHING. It renders two figures and a
 * caveat sentence; it does not hide the chart, disable the export, or
 * short-circuit its parent at any coverage level. A future "we should hide
 * the chart below X% coverage" change contradicts the §9 sign-off decision
 * — the whole point is that a thin-history org sees its real trajectory
 * next to an honest statement of how thin the history is.
 */
export function DataCoverageStrip({ coverage }: { coverage: SustainabilityCoverage }) {
  const { fleetAssetCount, coveredAssetCount, coveragePct, monthsAvailable, earliestPeriodKey, latestPeriodKey } =
    coverage;

  return (
    <div
      className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3"
      data-testid="data-coverage-strip"
    >
      <CoverageStat
        icon={CalendarRange}
        label="Months of history available"
        value={monthsAvailable.toLocaleString('en-US')}
        detail={
          earliestPeriodKey && latestPeriodKey
            ? `${earliestPeriodKey} through ${latestPeriodKey}`
            : 'No use-phase history computed yet'
        }
      />
      <CoverageStat
        icon={PieChart}
        label="Fleet covered"
        value={`${coveragePct.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`}
        detail={`${coveredAssetCount.toLocaleString('en-US')} of ${fleetAssetCount.toLocaleString('en-US')} assets have a computed use-phase figure`}
      />

      {/* The caveat itself — not a warning banner, not a blocker. */}
      <p className="max-w-xl text-xs leading-relaxed text-zinc-600">
        This trajectory is drawn from the history available today and deepens every month. Assets without
        a computed use-phase figure contribute nothing to the line above — that is a gap in coverage, not a
        finding that they emit nothing.
      </p>
    </div>
  );
}

function CoverageStat({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof CalendarRange;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 size-4 shrink-0 text-zinc-400" />
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="font-mono text-lg font-semibold tabular-nums leading-tight text-zinc-900">{value}</div>
        <div className="mt-0.5 text-xs text-zinc-500">{detail}</div>
      </div>
    </div>
  );
}
